import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    MagnifyingGlass, ArrowsClockwise, Check, PaperPlaneTilt, Calendar,
    InstagramLogo, FacebookLogo, LinkedinLogo, ThreadsLogo, YoutubeLogo,
    FileText, VideoCamera, ImageSquare, Envelope, Quotes, ChatCircle,
    Funnel, ShieldCheck, ShieldWarning, ShieldSlash, Warning,
} from "@phosphor-icons/react";

const COLUMNS = ["Draft", "Review", "Approved", "Scheduled", "Published", "Archived"];
const ASSET_ICON = {
    Instagram: InstagramLogo, Facebook: FacebookLogo, LinkedIn: LinkedinLogo,
    Threads: ThreadsLogo, YouTube: YoutubeLogo, TikTok: VideoCamera,
};
const TYPE_ICON = {
    Carousel: ImageSquare, VideoScript: VideoCamera, Blog: FileText,
    Email: Envelope, QuoteGraphic: Quotes, TextGraphic: ChatCircle,
};

export default function ProductionLine() {
    const nav = useNavigate();
    const [data, setData] = useState({ columns: {}, counts: {}, total: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [platform, setPlatform] = useState("all");
    const [goal, setGoal] = useState("all");
    const [validation, setValidation] = useState("all");
    const [selected, setSelected] = useState(new Set());
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

    const reload = async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (platform !== "all") params.platform = platform;
            if (goal !== "all") params.content_goal = goal;
            const r = await api.get("/content-studio/production-line", { params });
            // Optional client-side validation filter — keeps backend
            // surface area small while letting the operator slice the
            // board by validation state.
            if (validation !== "all") {
                const cols = {};
                Object.entries(r.data.columns || {}).forEach(([k, v]) => {
                    cols[k] = (v || []).filter(it => (it.validation_status || "skipped") === validation);
                });
                setData({
                    columns: cols,
                    counts: Object.fromEntries(Object.entries(cols).map(([k, v]) => [k, v.length])),
                    total: Object.values(cols).reduce((s, v) => s + v.length, 0),
                });
            } else {
                setData(r.data);
            }
        } catch {
            toast.error("Could not load Production Line");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (platform !== "all") params.platform = platform;
            if (goal !== "all") params.content_goal = goal;
            try {
                const r = await api.get("/content-studio/production-line", { params });
                if (cancelled) return;
                if (validation !== "all") {
                    const cols = {};
                    Object.entries(r.data.columns || {}).forEach(([k, v]) => {
                        cols[k] = (v || []).filter(it => (it.validation_status || "skipped") === validation);
                    });
                    setData({
                        columns: cols,
                        counts: Object.fromEntries(Object.entries(cols).map(([k, v]) => [k, v.length])),
                        total: Object.values(cols).reduce((s, v) => s + v.length, 0),
                    });
                } else {
                    setData(r.data);
                }
                setLoading(false);
            } catch {
                if (!cancelled) {
                    toast.error("Could not load Production Line");
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, platform, goal, validation]);

    const toggle = (id) => {
        setSelected(s => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const selectColumn = (col) => {
        const ids = (data.columns[col] || []).map(i => i.id);
        setSelected(s => {
            const n = new Set(s);
            const allIn = ids.every(id => n.has(id));
            if (allIn) ids.forEach(id => n.delete(id));
            else ids.forEach(id => n.add(id));
            return n;
        });
    };

    const clear = () => setSelected(new Set());

    const bulkTransition = async (status) => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const r = await api.post(`/content-studio/bulk/transition?status=${status}`, { content_ids: Array.from(selected) });
            const parts = [`${r.data.ok} moved to ${status}`];
            if (r.data.blocked) parts.push(`${r.data.blocked} blocked by fact validator`);
            if (r.data.skipped) parts.push(`${r.data.skipped} skipped`);
            const msg = parts.join(" · ");
            if (r.data.blocked) {
                toast.warning(msg, { duration: 7000, description: "Open the flagged items to review unsupported claims." });
            } else {
                toast.success(msg);
            }
            clear();
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk transition failed");
        } finally {
            setBusy(false);
        }
    };

    const bulkQueue = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const r = await api.post("/content-studio/bulk/queue", { content_ids: Array.from(selected) });
            const parts = [`${r.data.ok} queued to Buffer`];
            if (r.data.blocked) parts.push(`${r.data.blocked} blocked by fact validator`);
            if (r.data.errors?.length) parts.push(`${r.data.errors.length} failed`);
            const msg = parts.join(" · ");
            if (r.data.blocked) {
                toast.warning(msg, { duration: 7000, description: "Open flagged items to review unsupported claims." });
            } else {
                toast.success(msg);
            }
            if (r.data.errors?.length) {
                r.data.errors.slice(0, 3).forEach(e => toast.error(e.message, { duration: 6000 }));
            }
            clear();
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk queue failed");
        } finally {
            setBusy(false);
        }
    };

    // V2.5 — Publish All: one-button approve+queue chain. Closes the
    // largest manual-step gap by walking any selected draft/review/
    // approved items straight into Buffer's auto-queue.
    const publishAll = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(`Approve and queue ${selected.size} item${selected.size === 1 ? "" : "s"} to Buffer right now?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/content-studio/bulk/publish-now", { content_ids: Array.from(selected) });
            const parts = [`${r.data.queued} queued to Buffer`];
            if (r.data.blocked) parts.push(`${r.data.blocked} blocked by fact validator`);
            if (r.data.errors?.length) parts.push(`${r.data.errors.length} failed`);
            const msg = parts.join(" · ");
            if (r.data.blocked) {
                toast.warning(msg, { duration: 8000, description: "Unsupported-claim items were NOT published. Review them individually." });
            } else {
                toast.success(msg);
            }
            if (r.data.errors?.length) {
                r.data.errors.slice(0, 3).forEach(e => toast.error(e.message, { duration: 6000 }));
            }
            clear();
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish all failed");
        } finally {
            setBusy(false);
        }
    };

    const opts = useMemo(() => ({
        platforms: ["Instagram","Facebook","LinkedIn","Threads","YouTube","TikTok"],
        goals: ["Awareness","Understanding","Trust","Conversion","Partner Education","Community Discussion"],
    }), []);

    return (
        <div className="space-y-6" data-testid="production-line">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-bold leading-tight">Production Line</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[64ch]">
                        Every asset across every source, grouped by pipeline column. Multi-select to bulk-approve, queue to Buffer, or schedule for a specific time.
                    </p>
                </div>
                <Button data-testid="pl-refresh" variant="outline" size="sm" className="rounded-sm" onClick={reload}>
                    <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        data-testid="pl-search"
                        placeholder="Search assets…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-sm h-9 pl-8 w-64"
                    />
                </div>
                <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger data-testid="pl-platform" className="rounded-sm h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All platforms</SelectItem>
                        {opts.platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={goal} onValueChange={setGoal}>
                    <SelectTrigger data-testid="pl-goal" className="rounded-sm h-9 w-52 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All content goals</SelectItem>
                        {opts.goals.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={validation} onValueChange={setValidation}>
                    <SelectTrigger data-testid="pl-validation" className="rounded-sm h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All validation</SelectItem>
                        <SelectItem value="pass">Fact-check: pass</SelectItem>
                        <SelectItem value="needs_review">Needs review</SelectItem>
                        <SelectItem value="skipped">Not validated</SelectItem>
                        <SelectItem value="error">Validator error</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                    <Funnel size={12} className="inline mr-1" /> {data.total} total
                </span>
            </div>

            {/* Bulk-action bar */}
            {selected.size > 0 && (
                <div className="sticky top-2 z-30 flex items-center justify-between flex-wrap gap-3 border border-primary/30 bg-primary/5 rounded-sm px-4 py-2.5" data-testid="pl-bulk-bar">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-primary">{selected.size} selected</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Button data-testid="pl-bulk-publish-all" size="sm" className="rounded-sm h-7 px-2 text-[11px] bg-primary" onClick={publishAll} disabled={busy}>
                            <PaperPlaneTilt size={11} className="mr-1" weight="fill" /> Publish all to Buffer
                        </Button>
                        <Button data-testid="pl-bulk-review" size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[11px]" onClick={() => bulkTransition("Review")} disabled={busy}>
                            Move to Review
                        </Button>
                        <Button data-testid="pl-bulk-approve" size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[11px]" onClick={() => bulkTransition("Approved")} disabled={busy}>
                            <Check size={11} className="mr-1" /> Approve
                        </Button>
                        <Button data-testid="pl-bulk-queue" size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[11px]" onClick={bulkQueue} disabled={busy}>
                            <PaperPlaneTilt size={11} className="mr-1" /> Queue
                        </Button>
                        <Button data-testid="pl-bulk-schedule" size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[11px]" onClick={() => setScheduleOpen(true)} disabled={busy}>
                            <Calendar size={11} className="mr-1" /> Schedule…
                        </Button>
                        <Button data-testid="pl-bulk-archive" size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-[11px]" onClick={() => bulkTransition("Archived")} disabled={busy}>
                            Archive
                        </Button>
                        <Button data-testid="pl-bulk-clear" size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-[11px]" onClick={clear} disabled={busy}>
                            Clear
                        </Button>
                    </div>
                </div>
            )}

            {/* Columns */}
            {loading ? (
                <p className="text-sm text-muted-foreground">Loading production line…</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="pl-columns">
                    {COLUMNS.map(col => {
                        const items = data.columns[col] || [];
                        return (
                            <div key={col} className="border border-border bg-card rounded-sm p-3 min-h-[200px]" data-testid={`pl-col-${col}`}>
                                <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-display font-bold text-sm">{col}</h3>
                                        <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{items.length}</Badge>
                                    </div>
                                    {items.length > 0 && (
                                        <button
                                            type="button"
                                            data-testid={`pl-col-select-${col}`}
                                            onClick={() => selectColumn(col)}
                                            className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary"
                                        >
                                            select
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {items.length === 0 && (
                                        <p className="text-[11px] text-muted-foreground italic">No items in {col.toLowerCase()}</p>
                                    )}
                                    {items.map(it => (
                                        <Card
                                            key={it.id}
                                            item={it}
                                            apiBase={apiBase}
                                            selected={selected.has(it.id)}
                                            onToggle={() => toggle(it.id)}
                                            onOpen={() => it.source_asset_id && nav(`/content-studio/sources/${it.source_asset_id}`)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Bulk schedule dialog */}
            <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <DialogContent className="rounded-sm max-w-md" data-testid="pl-schedule-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Bulk schedule {selected.size} asset{selected.size === 1 ? "" : "s"}</DialogTitle>
                        <DialogDescription className="text-[12px]">
                            Each asset will be scheduled for the same UTC time on its platform&apos;s configured Buffer channel.
                        </DialogDescription>
                    </DialogHeader>
                    <BulkScheduleForm
                        ids={Array.from(selected)}
                        onDone={() => { setScheduleOpen(false); clear(); reload(); }}
                        onCancel={() => setScheduleOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ValidationBadge({ status, issues }) {
    if (!status || status === "skipped") {
        return (
            <span data-testid="pl-validation-badge" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-muted-foreground" title="Not validated against the Knowledge Base">
                <ShieldSlash size={10} weight="duotone" /> not validated
            </span>
        );
    }
    if (status === "pass") {
        return (
            <span data-testid="pl-validation-badge" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-emerald-700 dark:text-emerald-300" title="Every factual claim is supported by the Knowledge Base">
                <ShieldCheck size={10} weight="duotone" /> fact-check ok
            </span>
        );
    }
    if (status === "needs_review") {
        const n = (issues || []).length;
        return (
            <span data-testid="pl-validation-badge" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-red-700 dark:text-red-300" title="Unsupported claims found">
                <ShieldWarning size={10} weight="duotone" /> needs review{n ? ` (${n})` : ""}
            </span>
        );
    }
    return (
        <span data-testid="pl-validation-badge" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-700 dark:text-amber-300" title="Validator could not run">
            <Warning size={10} weight="duotone" /> validator error
        </span>
    );
}

function Card({ item, apiBase, selected, onToggle, onOpen }) {
    const PIcon = ASSET_ICON[item.platform] || TYPE_ICON[item.content_type] || FileText;
    const sub = item.source_title || "(no source)";
    const flagged = item.validation_status === "needs_review";
    return (
        <article
            className={`border rounded-sm p-2.5 bg-background transition-colors ${
                selected ? "border-primary bg-primary/5"
                : flagged ? "border-red-500/50 hover:border-red-500"
                : "border-border hover:border-primary/30"
            }`}
            data-testid={`pl-card-${item.id}`}
        >
            <div className="flex items-start gap-2">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={onToggle}
                    className="mt-1 shrink-0"
                    data-testid={`pl-check-${item.id}`}
                />
                <div className="flex-1 min-w-0">
                    {item.rendered_image_path && (
                        <img
                            src={`${apiBase}/content-studio/rendered/${item.rendered_image_path}`}
                            alt=""
                            className="w-full h-20 object-cover rounded-sm border border-border mb-2 cursor-pointer"
                            onClick={onOpen}
                        />
                    )}
                    <div className="flex items-center gap-1.5 mb-1">
                        <PIcon size={12} weight="duotone" className="text-primary shrink-0" />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                            {item.content_type || item.platform || "Asset"}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onOpen}
                        className="text-left w-full"
                    >
                        <p className="text-xs font-display font-semibold leading-snug line-clamp-2 hover:text-primary">{item.title}</p>
                    </button>
                    <p className="text-[10px] text-muted-foreground mt-1 truncate" title={sub}>↳ {sub}</p>
                    <div className="mt-1.5">
                        <ValidationBadge status={item.validation_status} issues={item.validation_issues} />
                        {item.validation_override_by && (
                            <span className="ml-1.5 text-[9px] font-mono uppercase tracking-wider text-amber-700 dark:text-amber-300" title={`Overridden by ${item.validation_override_by}: ${item.validation_override_reason || ""}`}>
                                · override
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}

function BulkScheduleForm({ ids, onDone, onCancel }) {
    const tomorrow9 = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        const pad = n => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    })();
    const [when, setWhen] = useState(tomorrow9);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const iso = new Date(when).toISOString();
            const r = await api.post("/content-studio/bulk/schedule", {
                content_ids: ids,
                scheduled_for: iso,
            });
            toast.success(`${r.data.ok} scheduled${r.data.errors?.length ? ` · ${r.data.errors.length} failed` : ""}`);
            if (r.data.errors?.length) {
                r.data.errors.slice(0, 3).forEach(e => toast.error(e.message, { duration: 6000 }));
            }
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk schedule failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">When (local time)</label>
                <input
                    type="datetime-local"
                    data-testid="pl-schedule-when"
                    value={when}
                    onChange={(e) => setWhen(e.target.value)}
                    className="w-full border border-border rounded-sm h-9 px-3 text-sm bg-background"
                />
            </div>
            <DialogFooter>
                <Button variant="ghost" size="sm" className="rounded-sm" onClick={onCancel}>Cancel</Button>
                <Button data-testid="pl-schedule-confirm" size="sm" className="rounded-sm" onClick={submit} disabled={busy}>
                    <Calendar size={12} className="mr-1.5" />
                    {busy ? "Scheduling…" : `Schedule ${ids.length}`}
                </Button>
            </DialogFooter>
        </div>
    );
}
